import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type {
  CollectingResultReportItem,
  GetCollectingResultReportsParams,
  GetCollectingResultReportsResponse,
} from '~/types/delivery-agency.types';

export const fetchCollectingResultReports = async (
  params: GetCollectingResultReportsParams
): Promise<GetCollectingResultReportsResponse> => {
  const { data } = await axiosInstance.get<GetCollectingResultReportsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_RESULT_REPORTS,
    { params }
  );
  return data;
};

type UseGetCollectingResultReportsQueryOptions = {
  /** 검색 전(null)에는 쿼리를 실행하지 않는다 */
  params: GetCollectingResultReportsParams | null;
  showGlobalDimOverlay?: boolean;
};

export const useGetCollectingResultReportsQuery = ({
  params,
  showGlobalDimOverlay = true,
}: UseGetCollectingResultReportsQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.collectingResultReports(params),
    queryFn: params ? () => fetchCollectingResultReports(params) : skipToken,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
    meta: { showGlobalDimOverlay },
  });
};

const mockItem = (
  index: number,
  status: CollectingResultReportItem['collectingShippingLabelStatus'],
  failureReason: CollectingResultReportItem['collectingFailureReasonCategory'] = null
): CollectingResultReportItem => ({
  id: 5000 + index,
  collectingRequestCode: `RR2026081${String(index).padStart(4, '0')}`,
  shippingLabelNumber: status.code === 'COLLECT_ACCEPTED' ? null : `CL2026081${String(index).padStart(4, '0')}`,
  originalOutboundCode: `OB2026081${String(index).padStart(4, '0')}`,
  originalOrderCodes: [`ORD-20260810-${String(index).padStart(4, '0')}`],
  collectingRequestedDate: '2026-08-17',
  collectingShippingLabelStatus: status,
  contact: { name: '홍**', tel: '010-****-1234' },
  collectingSite: {
    deliveryLocation: { code: 'DOORSTEP', description: '문 앞' },
    deliveryLocationDetail: '',
    refineAddress: '서울시 강남구 테헤란로 ***',
  },
  collectingFailureReasonCategory: failureReason,
});

registerMockAPI<GetCollectingResultReportsResponse>({
  method: 'get',
  endpoint: API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_RESULT_REPORTS,
  label: '회수 결과 리포트/목록·서머리',
  scenarios: [
    {
      name: '정상 응답 (전 상태 조합)',
      response: {
        data: {
          items: [
            mockItem(1, { code: 'COLLECT_ACCEPTED', description: '회수 예정' }),
            mockItem(6, { code: 'COLLECT_DEPARTURE_READY', description: '회수 출발 준비' }),
            mockItem(2, { code: 'COLLECT_ON_THE_WAY', description: '회수 중' }),
            mockItem(3, { code: 'COLLECT_COMPLETED', description: '회수 완료' }),
            mockItem(
              4,
              { code: 'COLLECT_FAILED', description: '회수 실패' },
              {
                code: 'PRODUCT_NOT_FOUND',
                description: '회수 장소에 상품 없음',
              }
            ),
            mockItem(5, { code: 'COLLECT_CANCELED', description: '회수 취소' }),
          ],
          summary: {
            totalCount: 6,
            collectAcceptedCount: 1,
            collectDepartureReadyCount: 1,
            collectOnTheWayCount: 1,
            collectCompletedCount: 1,
            collectFailedCount: 1,
            collectCanceledCount: 1,
          },
        },
        pagination: { cursor: 1, listView: 50, listTotal: 6 },
      },
    },
    {
      name: '여러 페이지 (총 120건 중 1쪽)',
      response: {
        data: {
          items: Array.from({ length: 50 }, (_, index) =>
            mockItem(index + 1, { code: 'COLLECT_COMPLETED', description: '회수 완료' })
          ),
          summary: {
            totalCount: 120,
            collectAcceptedCount: 7,
            collectDepartureReadyCount: 3,
            collectOnTheWayCount: 20,
            collectCompletedCount: 70,
            collectFailedCount: 15,
            collectCanceledCount: 5,
          },
        },
        pagination: { cursor: 1, listView: 50, listTotal: 120 },
      },
    },
    {
      name: '검색 결과 없음',
      response: {
        data: {
          items: [],
          summary: {
            totalCount: 0,
            collectAcceptedCount: 0,
            collectDepartureReadyCount: 0,
            collectOnTheWayCount: 0,
            collectCompletedCount: 0,
            collectFailedCount: 0,
            collectCanceledCount: 0,
          },
        },
        pagination: { cursor: 1, listView: 50, listTotal: 0 },
      },
    },
    {
      name: '서버 에러 (500)',
      status: 500,
      response: {} as GetCollectingResultReportsResponse,
    },
  ],
});
